/* hero-animation.js — Sneak Freak (AphCom) · signature motion (prototype animations.js, without GSAP)
   <sf-hero>          eyebrow fade-down + slogan clip-up (CSS transitions toggled by .is-in) and the SF monogram
                      write-on (stroke guides drawn at constant pen speed, nib riding the ink) once the hero is in
                      view and the tab is visible; settles to the resting opacity.
   <sf-image-banner>  the same signature write-on for the spotlight/editorial monogram, on scroll-in.
   <sf-mono-mark>     the same write-on for a standalone mark (the collection header), on scroll-in.
   <sf-trust-strip>   shield outline + check stroke draw-in on scroll-in.
   Everything honours theme.reducedMotion / html.motion-ok (finished state, nothing hidden) and recovers if the
   animation clock never runs (background tab). Requires theme.js. */
'use strict';
(function () {
  const theme = window.theme || (window.theme = {});
  const motionOk = () => !theme.reducedMotion && document.documentElement.classList.contains('motion-ok');
  const whenVisible = (fn) => {
    if (!document.hidden) { fn(); return; }
    const onVis = () => { if (document.hidden) return; document.removeEventListener('visibilitychange', onVis); fn(); };
    document.addEventListener('visibilitychange', onVis);
  };
  const onIntersect = (el, threshold, fn, failSafeMs) => {
    let fired = false;
    const go = () => { if (fired) return; fired = true; fn(); };
    if (!('IntersectionObserver' in window)) { go(); return; }
    const io = new IntersectionObserver((entries) => { entries.forEach((e) => { if (e.isIntersecting) { io.disconnect(); go(); } }); }, { threshold });
    io.observe(el);
    if (failSafeMs) setTimeout(() => { io.disconnect(); go(); }, failSafeMs);
  };

  /* ---------------- monogram write-on ---------------- */
  const PEN_SPEED = 430; /* user units per second */
  const LIFT_MS = 70;
  const REST_OPACITY = '0.42';
  function settleMark(mark, atRest) {
    if (!mark) return;
    mark.querySelectorAll('.sf-ink').forEach((g) => { g.style.strokeDasharray = 'none'; g.style.strokeDashoffset = '0'; g.style.opacity = '1'; });
    const nib = mark.querySelector('.sf-nib');
    if (nib) nib.style.opacity = '0';
    /* the written mark ends at REST; one that skips the animation has to match, or the hero looks different
       depending on how you arrived at it */
    if (atRest) mark.style.opacity = REST_OPACITY;
    mark.classList.remove('is-writing');
  }
  function writeMark(mark) {
    if (!mark || mark.dataset.written) return;
    mark.dataset.written = '1';
    const guides = Array.from(mark.querySelectorAll('.sf-ink'));
    const nib = mark.querySelector('.sf-nib');
    if (!guides.length || !motionOk()) { settleMark(mark); return; }
    const strokes = guides.map((g) => {
      let len = 0;
      try { len = g.getTotalLength(); } catch (e) { len = 0; }
      g.style.strokeDasharray = String(len);
      g.style.strokeDashoffset = String(len);
      g.style.opacity = '0';
      return { el: g, len, dur: Math.max(180, (len / PEN_SPEED) * 1000) };
    });
    mark.classList.add('is-writing');
    let done = false;
    const total = strokes.reduce((s, x) => s + x.dur + LIFT_MS, 0) + 1400;
    /* Resting opacity of the finished mark (prototype REST): the ink is drawn at full strength, then settles. */
    const REST = REST_OPACITY;
    const rest = () => { mark.style.transition = 'opacity 1100ms cubic-bezier(.45,0,.55,1)'; mark.style.opacity = REST; mark.classList.remove('is-writing'); };
    const finish = () => {
      if (done) return;
      done = true;
      settleMark(mark);
      rest();
    };
    let i = 0;
    const next = () => {
      if (i >= strokes.length) {
        if (nib) { nib.style.transition = 'opacity 250ms ease-out'; nib.style.opacity = '0'; }
        setTimeout(() => { done = true; rest(); }, 120);
        return;
      }
      const s = strokes[i++];
      const start = performance.now();
      s.el.style.opacity = '1';
      if (nib) nib.style.opacity = '0.9';
      const step = (now) => {
        if (done) return;
        const p = Math.min(1, (now - start) / s.dur);
        const covered = s.len * p;
        s.el.style.strokeDashoffset = String(s.len - covered);
        if (nib) {
          try { const pt = s.el.getPointAtLength(Math.min(covered, s.len)); nib.setAttribute('cx', pt.x); nib.setAttribute('cy', pt.y); } catch (e) { /* detached */ }
        }
        if (p < 1) requestAnimationFrame(step); else setTimeout(next, LIFT_MS);
      };
      requestAnimationFrame(step);
    };
    next();
    /* Recovery: a paused animation clock (background tab) would leave the strokes transparent. */
    setTimeout(finish, total + 2500);
  }

  /* ---------------- sf-hero ---------------- */
  class SFHero extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      const lines = this.querySelectorAll('.hero__slogan-line > span');
      lines.forEach((el, idx) => { el.style.transitionDelay = (idx * 120) + 'ms'; });
      /* Intro: CSS holds the eyebrow/lines offset while sf-hero lacks .is-in (motion-ok only); two frames in, release. */
      if (motionOk()) requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-in')));
      else this.classList.add('is-in');
      /* the old-logo image (hero.liquid): reveal it once, unless the page transition just showed it */
      const logo = this.querySelector('.hero__logo');
      if (logo) logo.classList.add(this.dataset.writeOn === 'true' && motionOk() && !window.__sfArrived ? 'is-writing' : 'is-done');
      const mark = this.querySelector('.hero__monogram');
      if (!mark) return;
      /* arrived through the page transition: it just wrote the same signature — show the finished mark instead of replaying it */
      if (this.dataset.writeOn !== 'true' || !motionOk() || window.__sfArrived) { settleMark(mark, true); return; }
      /* The reference waits for load AND for the hero to be in view, because a write that runs before the
         photo paints is over before anyone can see it (prototype animations.js, sfScheduleMonogram). Same rule
         here, but bounded: a store whose load event is slow still gets the signature within 2.5s. */
      let armed = false;
      const arm = () => { if (armed) return; armed = true; onIntersect(this, 0.25, () => whenVisible(() => setTimeout(() => writeMark(mark), 260)), 2500); };
      if (document.readyState === 'complete') arm();
      else { window.addEventListener('load', arm, { once: true }); setTimeout(arm, 2500); }
    }
  }

  /* ---------------- sf-image-banner ---------------- */
  class SFImageBanner extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.classList.add('is-in');
      const mark = this.querySelector('.image-banner__mono .monogram--write-on, .image-banner__mono-svg');
      if (!mark || !mark.querySelector('.sf-ink')) return;
      if (this.dataset.writeOn !== 'true' || !motionOk()) { settleMark(mark); return; }
      onIntersect(mark, 0.35, () => whenVisible(() => setTimeout(() => writeMark(mark), 220)), 6000);
    }
  }

  /* ---------------- sf-mono-mark ----------------
     A standalone signature mark anywhere on the page, drawn by exactly the same writeMark() the hero and the
     image banner use — same guides, same pen speed, same nib, same resting settle. It exists so the collection
     header does not need a second, unrelated animation.

     The element never sets its OWN opacity: writeMark() puts the resting value inline on the <svg>, and the
     page fades the wrapper. That keeps "is it drawn yet" and "is the header open" as two separate questions,
     so a header collapsing mid-draw cannot corrupt the drawing, and reopening never replays it. */
  class SFMonoMark extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      const mark = this.querySelector('.monogram--write-on');
      if (!mark || !mark.querySelector('.sf-ink')) return;
      if (this.dataset.writeOn !== 'true' || !motionOk()) { settleMark(mark); return; }
      onIntersect(mark, 0.2, () => whenVisible(() => setTimeout(() => writeMark(mark), 160)), 6000);
    }
  }

  /* ---------------- sf-trust-strip (shield draw) ---------------- */
  class SFTrustStrip extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.classList.add('is-in');
      const outline = this.querySelector('.authenticity__shield-outline');
      const check = this.querySelector('.authenticity__shield-check');
      if (!check || !motionOk()) return;
      const paths = [outline, check].filter(Boolean);
      paths.forEach((p) => { p.style.strokeDasharray = '1'; p.style.strokeDashoffset = '1'; });
      onIntersect(this, 0.4, () => whenVisible(() => {
        if (outline) { outline.style.transition = 'stroke-dashoffset 600ms cubic-bezier(.22,1,.36,1)'; outline.style.strokeDashoffset = '0'; }
        check.style.transition = 'stroke-dashoffset 350ms cubic-bezier(.22,1,.36,1) ' + (outline ? '450ms' : '0ms');
        check.style.strokeDashoffset = '0';
      }), 4000);
      /* Recovery for a paused clock: snap to drawn. */
      setTimeout(() => paths.forEach((p) => { p.style.strokeDashoffset = '0'; }), 8000);
    }
  }

  if (!customElements.get('sf-hero')) customElements.define('sf-hero', SFHero);
  if (!customElements.get('sf-image-banner')) customElements.define('sf-image-banner', SFImageBanner);
  if (!customElements.get('sf-trust-strip')) customElements.define('sf-trust-strip', SFTrustStrip);
  if (!customElements.get('sf-mono-mark')) customElements.define('sf-mono-mark', SFMonoMark);
})();
